import { gql, useMutation, useQuery } from '@apollo/client'
import { isCustomerCloak } from '../utils/cloak'
import { CustomerTypeEnum, Mutation, Query, UserContext } from '../gen/models'

/**
 * The Empty Catalog data layer (STORY 7358, spec §1/§3.2). Two documents, two hooks and ONE predicate,
 * consumed only by `routes/SupplierPortal/EmptyCatalog.tsx` and `routes/components/MainSideBar.tsx` —
 * imported by DIRECT PATH and deliberately absent from the `api/hooks/index.ts` barrel (the
 * `batchResults.tsx` / `opportunitiesHero.tsx` precedent). Being non-barrelled is load-bearing: a
 * `jest.mock` on a barrelled module replaces the WHOLE module, so `Catalog.test.tsx`'s and
 * `SupplierPortalRouting.test.tsx`'s factories would each owe an entry for every hook added here.
 *
 * ⚠️ `ME_QUERY` (`users.tsx`) is UNTOUCHED BY DESIGN. This module does not widen it; it issues its own
 * narrow `me` read instead, and the reason is the coupling below.
 *
 * ⚠️⚠️ THE FETCH POLICY AND THE SELECTION CHANGE TOGETHER OR NOT AT ALL.
 * `me` returns `UserContext`, an ID-LESS object, and the app declares no `typePolicies`/`keyFields`
 * (`ApolloClient.tsx`). Apollo's `InMemoryCache` therefore stores it INLINE under `ROOT_QUERY.me` and
 * REPLACES it on every write of that field. This document selects a strict SUBSET of what `ME_QUERY`
 * selects, so under any CACHING policy it would overwrite what `ME_QUERY` wrote — dropping `isAdmin`
 * and `powerUserList` from the cached object. `MeContextProvider` watches `ME_QUERY` `cache-first` and
 * renders `<PageLoading />` APP-WIDE while it re-fetches, so one gate fetch would blank the entire app
 * for a cloaked admin. `no-cache` is the only Apollo policy that does NOT write results (`network-only`
 * still writes). The document also deliberately selects NO `id` — under `no-cache` an id buys nothing,
 * and adding one would make `User` normalizable and reintroduce the other half of the hazard.
 * Moving the policy to a caching one re-arms BOTH hazards at once; it owes a selection review and a
 * `MeContext` regression pass.
 */

/** The server's role literal (`catalog.service.ts`), exported once so a test can assert it without re-typing. */
export const CUSTOMER_SUCCESS_ROLE = 'customer-success'

/**
 * No arguments, no variables, no selection beyond the root field — `Mutation.resetCatalog` is a
 * `Boolean!`, so a selection set is not possible. Byte-identical in shape to what Appian issues.
 */
export const RESET_CATALOG = gql`
  mutation resetCatalog {
    resetCatalog
  }
`

/**
 * The gate document. Selects exactly the two terms the server's own permission check reads — the role
 * and the cloak markers — and nothing else. See the ⚠️⚠️ coupling note above before touching either the
 * selection or the policy in `useEmptyCatalogGateQuery`.
 */
export const EMPTY_CATALOG_GATE_QUERY = gql`
  query emptyCatalogGate {
    me {
      user {
        role
        cloakUserId
        cloakCustomerId
      }
    }
  }
`

/**
 * No options: no `refetchQueries`, no `onCompleted`. The page owns no data, and every listing the reset
 * affects is already `network-only`, so it shows the post-delete state on its next visit. The default
 * `errorPolicy` makes a GraphQL error REJECT the promise — the shipped `useTierArchivedToggleMutation`
 * behaviour.
 */
export const useResetCatalogMutation = () => useMutation<Pick<Mutation, 'resetCatalog'>>(RESET_CATALOG)

/**
 * The gate read. Both options are FIXED HERE and are never caller-supplied, so the two consumers cannot
 * diverge on *when* the document fires: it is skipped outright for any principal who is not
 * customer-cloaked, which is what keeps hospital users, plain suppliers and supplier admins at zero
 * new requests.
 */
export const useEmptyCatalogGateQuery = (me: UserContext | undefined) =>
  useQuery<Pick<Query, 'me'>>(EMPTY_CATALOG_GATE_QUERY, {
    skip: !isCustomerCloak(me?.user),
    fetchPolicy: 'no-cache',
  })

/**
 * The predicate — the server's terms (`catalog.service.ts`), and nothing else. Applied at BOTH the
 * nav child and the page, so the two can never disagree.
 *
 * ⚠️ **AMENDED BY STORY 7357 (R5, AC 33) — the two terms are now the server's `OR`.** The ROLE arm is the
 * pre-existing #7358 one, kept verbatim so Appian's customer-success population is unaffected while Appian
 * stays live; the SUPPLIER arm is #7357's, admitting a Curvo admin cloaked into a supplier customer.
 *
 * `isCustomerCloak(me?.user)` stays the FIRST conjunct, so every non-cloaked and every hospital-cloaked
 * principal is `false` exactly as before — the added arm can only admit someone who already passed it.
 *
 * A skipped, in-flight or errored gate yields `gate === undefined` and therefore `false` **for the role
 * arm**; the supplier arm needs no gate at all, so a cloaked supplier-typed admin is admitted while the
 * gate is still pending. (Because a customer-scoped cloak can only target a supplier, the gate's answer is
 * decision-inert for every reachable principal after this change — the literal OR is kept to mirror the
 * server. Retiring the gate document is #7358's follow-up, deliberately not opened here.)
 */
export const canEmptyCatalog = (me: UserContext | undefined, gate: Pick<Query, 'me'> | undefined): boolean => {
  const isSupplier = me?.user.customer.type === CustomerTypeEnum.Supplier
  return isCustomerCloak(me?.user) && (isSupplier || gate?.me.user.role === CUSTOMER_SUCCESS_ROLE)
}
